import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { stripHtml } from "../api/homePage";
import { aboutGrid } from "../assets/appImages";

const POINT_BORDERS = [
  "border-[#B8E4F5]",
  "border-[#B8E8C8]",
  "border-[#F5C4C4]",
  "border-[#D4C4E8]",
];

const isSvgUrl = (url = "") => /\.svg($|\?)/i.test(url);

const AboutImage = ({ src, fallback, alt }) => {
  const [imageSrc, setImageSrc] = useState(fallback || "");

  useEffect(() => {
    let objectUrl = "";
    let isMounted = true;

    const loadImage = async () => {
      if (!src) {
        if (isMounted) setImageSrc(fallback || "");
        return;
      }

      // API Active Storage often serves SVG as attachment/octet-stream,
      // which browsers won't render in <img>. Re-wrap as image/svg+xml.
      if (isSvgUrl(src)) {
        try {
          const response = await fetch(src);
          if (!response.ok) throw new Error("Failed to load about image");
          const svgText = await response.text();
          const blob = new Blob([svgText], { type: "image/svg+xml" });
          objectUrl = URL.createObjectURL(blob);
          if (isMounted) setImageSrc(objectUrl);
          return;
        } catch {
          if (isMounted) setImageSrc(fallback || "");
          return;
        }
      }

      if (isMounted) setImageSrc(src);
    };

    loadImage();

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, fallback]);

  if (!imageSrc) return null;

  return (
    <img
      src={imageSrc}
      alt={alt}
      referrerPolicy="no-referrer"
      onError={() => {
        if (fallback && imageSrc !== fallback) {
          setImageSrc(fallback);
        }
      }}
      className="block h-auto w-full max-w-md object-contain sm:max-w-lg lg:max-w-xl"
    />
  );
};

const AboutUs = ({ about, showReadMore = true }) => {
  const title = about?.title || "";
  const description = about?.description ? stripHtml(about.description) : "";
  const aboutImage = about?.images?.[0] || "";
  const featureItems = Array.isArray(about?.points) ? about.points : [];

  if (!about) return null;

  return (
    <section className="bg-white py-16 md:py-20 lg:py-24">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 md:px-8 lg:grid-cols-2 lg:gap-14">
        <div className="relative flex min-h-[320px] w-full justify-center lg:justify-start">
          <AboutImage src={aboutImage} fallback={aboutGrid} alt="About Dr Yash Gulati" />
        </div>

        <div className="text-left">
          <p className="text-[18px] font-bold tracking-wide text-brand-orange">About Us</p>
          <h2 className="mt-2 text-3xl font-bold leading-tight text-brand-purple md:text-4xl lg:text-[2.5rem]">
            {title}
          </h2>
          <p className="mt-5 whitespace-pre-line text-sm font-medium leading-relaxed text-[#444444] md:text-base">
            {description}
          </p>

          {featureItems.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {featureItems.map(({ id, label, icon }, index) => (
                <div
                  key={id || label}
                  className={`flex items-center gap-4 rounded-[10px] border bg-white px-4 py-3 shadow-[0_2px_12px_rgba(0,0,0,0.04)] ${
                    POINT_BORDERS[index % POINT_BORDERS.length]
                  }`}
                >
                  {icon ? (
                    <img
                      src={icon}
                      alt=""
                      aria-hidden="true"
                      referrerPolicy="no-referrer"
                      className="h-12 w-12 shrink-0 object-contain"
                    />
                  ) : null}
                  <span className="text-[16px] font-bold leading-tight text-brand-purple">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          ) : null}

          {showReadMore && (
            <Link
              to="/about"
              className="mt-8 inline-block rounded-lg bg-[#6C1376] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-brand-purple-dark"
            >
              Read More
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
